import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  fetchHuayJackpotPlayConfig,
  purchaseHuayJackpotTicket,
  type HuayJackpotPlayConfig,
  type HuayJackpotTicket,
} from '../../services/huayJackpotService'
import './huayJackpot.css'

const formatBaht = (value: number | null | undefined) =>
  Number(value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function describeTierPayout(
  tier: HuayJackpotPlayConfig['prizeTiers'][number],
  ticketPrice: number,
  displayJackpot: number,
) {
  if (tier.payoutMode === 'jackpot') return `${formatBaht(displayJackpot)} ฿`
  if (tier.payoutMode === 'fixed') return `${formatBaht(tier.fixedAmount)} ฿`
  return `${formatBaht(ticketPrice * Number(tier.multiplier || 0))} ฿`
}

const HuayJackpotLobbyPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [buying, setBuying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [config, setConfig] = useState<HuayJackpotPlayConfig | null>(null)
  const [whiteNumbers, setWhiteNumbers] = useState<number[]>([])
  const [redNumber, setRedNumber] = useState<number | null>(null)
  const [result, setResult] = useState<HuayJackpotTicket | null>(null)
  const [displayJackpot, setDisplayJackpot] = useState(0)
  const [rulesOpen, setRulesOpen] = useState(false)

  const whitePick = config?.rules?.whitePick ?? 5
  const whiteMax = config?.rules?.whiteMax ?? 69
  const redMax = config?.rules?.redMax ?? 26

  useEffect(() => {
    if (!rulesOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRulesOpen(false)
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [rulesOpen])

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetchHuayJackpotPlayConfig()
      setConfig(res.data)
      setDisplayJackpot(Number(res.data?.displayJackpotThb || 0))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'โหลดการตั้งค่าไม่สำเร็จ')
      setConfig(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadConfig()
  }, [loadConfig])

  const canBuy = useMemo(() => {
    if (!config?.salesOpen) return false
    if (buying) return false
    if (whiteNumbers.length !== whitePick) return false
    if (redNumber == null) return false
    return true
  }, [config?.salesOpen, buying, whiteNumbers.length, whitePick, redNumber])

  const toggleWhite = (n: number) => {
    setResult(null)
    setWhiteNumbers((prev) => {
      if (prev.includes(n)) return prev.filter((x) => x !== n)
      if (prev.length >= whitePick) return prev
      return [...prev, n].sort((a, b) => a - b)
    })
  }

  const pickRed = (n: number) => {
    setResult(null)
    setRedNumber((prev) => (prev === n ? null : n))
  }

  const quickPick = () => {
    setResult(null)
    const pool = Array.from({ length: whiteMax }, (_, i) => i + 1)
    for (let i = pool.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[pool[i], pool[j]] = [pool[j], pool[i]]
    }
    setWhiteNumbers(pool.slice(0, whitePick).sort((a, b) => a - b))
    setRedNumber(1 + Math.floor(Math.random() * redMax))
  }

  const clearPicks = () => {
    setResult(null)
    setWhiteNumbers([])
    setRedNumber(null)
  }

  const handlePurchase = async () => {
    if (!canBuy || redNumber == null) return
    setBuying(true)
    setError(null)
    try {
      const res = await purchaseHuayJackpotTicket({
        whiteNumbers,
        redNumber,
      })
      setResult(res.data.ticket)
      if (res.data.displayJackpotThb != null) {
        setDisplayJackpot(Number(res.data.displayJackpotThb))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ซื้อตั๋วไม่สำเร็จ')
    } finally {
      setBuying(false)
    }
  }

  if (loading) {
    return (
      <div className="hj-page">
        <div className="hj-loading">กำลังโหลดเกมหวยแจ็กพอต...</div>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="hj-page">
        {error ? <div className="hj-error">{error}</div> : null}
        <button type="button" className="hj-btn hj-btn--secondary" onClick={() => void loadConfig()}>
          ลองใหม่
        </button>
      </div>
    )
  }

  const pickedSlots = Array.from({ length: whitePick }, (_, i) => whiteNumbers[i] ?? null)

  return (
    <div className="hj-page">
      <header className="hj-hero" aria-label="เกมหวยแจ็กพอต">
        <div className="hj-hero__stage" aria-hidden>
          <div className="hj-hero__sunburst" />
          <span className="hj-hero__orb hj-hero__orb--green">2</span>
          <span className="hj-hero__orb hj-hero__orb--blue">4</span>
          <span className="hj-hero__orb hj-hero__orb--red">7</span>
          <span className="hj-hero__cash hj-hero__cash--1">฿</span>
          <span className="hj-hero__cash hj-hero__cash--2">฿</span>
          <span className="hj-hero__cash hj-hero__cash--3">฿</span>
          <span className="hj-hero__ticket hj-hero__ticket--l" />
          <span className="hj-hero__ticket hj-hero__ticket--r" />
        </div>

        <div className="hj-hero__card">
          <button
            type="button"
            className="hj-rules-btn"
            onClick={() => setRulesOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={rulesOpen}
          >
            <i className="fas fa-book-open" aria-hidden />
            กติกา
          </button>

          <div className="hj-hero__card-top">
            <p className="hj-hero__eyebrow">เกมหวยแจ็กพอต</p>
            <div className="hj-logo-balls" aria-label="Huay Jackpot">
              {['H', 'U', 'A', 'Y', '★'].map((ch) => (
                <span key={ch} className="hj-logo-ball hj-logo-ball--white">
                  {ch}
                </span>
              ))}
              <span className="hj-logo-ball hj-logo-ball--red">JP</span>
            </div>
            <h1 className="hj-hero__brand">
              HUAY <span>JACKPOT</span>
            </h1>
            <div className="hj-logo-badges">
              <span className="hj-logo-badge">ออกผลทันที</span>
              <span className="hj-logo-badge hj-logo-badge--alt">เลือก 5+1</span>
              <span className="hj-logo-badge">ชำระเงินสด</span>
            </div>
          </div>

          <div className="hj-hero__card-bot">
            <div className="hj-jackpot">
              <span className="hj-jackpot__label">แจ็กพอตปัจจุบัน</span>
              <span className="hj-jackpot__value">{formatBaht(displayJackpot)}</span>
              <span className="hj-jackpot__currency">บาท</span>
            </div>
            <div className="hj-draw-strip" aria-hidden>
              {[12, 24, 35, 48, 61].map((n) => (
                <span key={n} className="hj-draw-ball hj-draw-ball--white">
                  {String(n).padStart(2, '0')}
                </span>
              ))}
              <span className="hj-draw-ball hj-draw-ball--red">07</span>
            </div>
            <p className="hj-hero__format-hint">รูปแบบเลขขาว 5 ลูก + เลขแดง 1 ลูก</p>
          </div>
        </div>
      </header>

      <div className="hj-meta">
        <span>ราคาตั๋ว {formatBaht(config.ticketPrice)} ฿</span>
        <span>{config.salesOpen ? 'เปิดขาย' : 'ปิดขายชั่วคราว'}</span>
        <span>
          กฎ {whitePick}+1 · ขาว 1–{whiteMax} · แดง 1–{redMax}
        </span>
        <button type="button" className="hj-meta__link" onClick={() => setRulesOpen(true)}>
          ดูกติกาเต็ม
        </button>
      </div>

      {error ? <div className="hj-error">{error}</div> : null}

      {result ? (
        <section className="hj-result" aria-live="polite">
          <span
            className={`hj-result__badge${
              result.isJackpot ? ' is-jackpot' : Number(result.prizeAmount) > 0 ? ' is-win' : ''
            }`}
          >
            {result.isJackpot
              ? 'แจ็กพอต!'
              : Number(result.prizeAmount) > 0
                ? 'ถูกรางวัล'
                : 'ไม่ถูกรางวัล'}
          </span>
          <p className="hj-result__amount">{formatBaht(result.prizeAmount)} ฿</p>
          <p className="hj-result__tier">
            {result.tierLabel || 'ไม่เข้าเงื่อนไขรางวัล'}
            {result.whiteMatch != null
              ? ` · ขาวตรง ${result.whiteMatch}${result.redMatch ? ' + แดง' : ''}`
              : ''}
          </p>

          <div className="hj-result__row">
            <span className="hj-result__label">เลขที่เลือก</span>
            {(result.whiteNumbers || []).map((n) => (
              <span key={`p-w-${n}`} className="hj-ball hj-ball--white is-selected">
                {n}
              </span>
            ))}
            <span className="hj-ball hj-ball--red is-selected">{result.redNumber}</span>
          </div>
          <div className="hj-result__row">
            <span className="hj-result__label">ผลออกรางวัล</span>
            {(result.drawnWhite || []).map((n) => (
              <span key={`d-w-${n}`} className="hj-ball hj-ball--white">
                {n}
              </span>
            ))}
            <span className="hj-ball hj-ball--red">{result.drawnRed}</span>
          </div>

          <div className="hj-actions" style={{ justifyContent: 'center' }}>
            <button type="button" className="hj-btn hj-btn--primary" onClick={clearPicks}>
              เล่นอีกครั้ง
            </button>
            <Link to="/history/games/huay-jackpot" className="hj-btn hj-btn--ghost">
              ดูประวัติ
            </Link>
          </div>
        </section>
      ) : null}

      <section className="hj-panel">
        <h2 className="hj-panel__title">เลขที่เลือก ({whiteNumbers.length}/{whitePick} + แดง)</h2>
        <p className="hj-panel__hint">แตะเลขด้านล่างเพื่อเลือก · กดซ้ำเพื่อยกเลิก</p>
        <div className="hj-picked" aria-label="เลขที่เลือก">
          {pickedSlots.map((n, i) =>
            n != null ? (
              <button
                key={`picked-w-${n}`}
                type="button"
                className="hj-ball hj-ball--white is-selected"
                onClick={() => toggleWhite(n)}
              >
                {n}
              </button>
            ) : (
              <span key={`empty-w-${i}`} className="hj-ball hj-ball--ghost">
                ?
              </span>
            ),
          )}
          {redNumber != null ? (
            <button
              type="button"
              className="hj-ball hj-ball--red is-selected"
              onClick={() => pickRed(redNumber)}
            >
              {redNumber}
            </button>
          ) : (
            <span className="hj-ball hj-ball--ghost">R</span>
          )}
        </div>
        <div className="hj-actions">
          <button type="button" className="hj-btn hj-btn--secondary" onClick={quickPick}>
            สุ่มเลข
          </button>
          <button type="button" className="hj-btn hj-btn--ghost" onClick={clearPicks}>
            ล้าง
          </button>
          <button
            type="button"
            className="hj-btn hj-btn--primary"
            disabled={!canBuy}
            onClick={() => void handlePurchase()}
          >
            {buying ? 'กำลังซื้อ...' : `ซื้อตั๋ว ${formatBaht(config.ticketPrice)} ฿`}
          </button>
        </div>
      </section>

      <section className="hj-panel">
        <h2 className="hj-panel__title">เลือกเลขขาว (1–{whiteMax})</h2>
        <p className="hj-panel__hint">เลือกได้ {whitePick} เลข ไม่ซ้ำ</p>
        <div className="hj-grid">
          {Array.from({ length: whiteMax }, (_, i) => i + 1).map((n) => (
            <button
              key={`w-${n}`}
              type="button"
              className={`hj-ball hj-ball--white${whiteNumbers.includes(n) ? ' is-selected' : ''}`}
              onClick={() => toggleWhite(n)}
              disabled={!whiteNumbers.includes(n) && whiteNumbers.length >= whitePick}
            >
              {n}
            </button>
          ))}
        </div>
      </section>

      <section className="hj-panel">
        <h2 className="hj-panel__title">เลือกเลขแดง (1–{redMax})</h2>
        <p className="hj-panel__hint">เลือกได้ 1 เลข</p>
        <div className="hj-grid hj-grid--red">
          {Array.from({ length: redMax }, (_, i) => i + 1).map((n) => (
            <button
              key={`r-${n}`}
              type="button"
              className={`hj-ball hj-ball--red${redNumber === n ? ' is-selected' : ''}`}
              onClick={() => pickRed(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </section>

      <section className="hj-panel">
        <h2 className="hj-panel__title">ตารางรางวัล</h2>
        <p className="hj-panel__hint">อ้างอิงราคาตั๋วปัจจุบัน · แจ็กพอตตามกองจริง</p>
        <div className="hj-tiers">
          {(config.prizeTiers || []).map((tier) => (
            <div key={tier.id} className="hj-tier-row">
              <span>
                {tier.label || `${tier.whiteMatch} ขาว${tier.redMatch ? ' + แดง' : ''}`}
              </span>
              <strong>{describeTierPayout(tier, config.ticketPrice, displayJackpot)}</strong>
            </div>
          ))}
        </div>
      </section>

      <div className="hj-actions">
        <Link to="/history/games/huay-jackpot" className="hj-btn hj-btn--ghost">
          <i className="fas fa-history" aria-hidden /> ประวัติหวยแจ็กพอต
        </Link>
        <button type="button" className="hj-btn hj-btn--ghost" onClick={() => setRulesOpen(true)}>
          <i className="fas fa-book-open" aria-hidden /> กติกา
        </button>
      </div>

      {rulesOpen ? (
        <div
          className="hj-rules-backdrop"
          role="presentation"
          onClick={() => setRulesOpen(false)}
        >
          <div
            className="hj-rules-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hj-rules-title"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="hj-rules-modal__header">
              <div>
                <p className="hj-rules-modal__eyebrow">HuayJackpot</p>
                <h2 id="hj-rules-title">กติกาเกมหวยแจ็กพอต</h2>
              </div>
              <button
                type="button"
                className="hj-rules-modal__close"
                aria-label="ปิด"
                onClick={() => setRulesOpen(false)}
              >
                <i className="fas fa-times" aria-hidden />
              </button>
            </header>

            <div className="hj-rules-modal__body">
              <section className="hj-rules-section">
                <h3>กติกาการเล่น</h3>
                <ol>
                  <li>
                    เลือกเลขขาว {whitePick} ตัว จาก 1–{whiteMax} (ห้ามซ้ำ) และเลขแดง 1 ตัว จาก 1–{redMax}
                  </li>
                  <li>ยืนยันซื้อ — ระบบหักเงินสด wallet ตามราคาตั๋ว ({formatBaht(config.ticketPrice)} ฿)</li>
                  <li>ออกผลทันทีฝั่งเซิร์ฟเวอร์ ไม่ต้องรอรอบเวลา</li>
                  <li>ถ้าถูกรางวัล เงินรางวัลเครดิตเข้า wallet อัตโนมัติ</li>
                </ol>
              </section>

              <section className="hj-rules-section">
                <h3>กติกาการออกรางวัล</h3>
                <ul>
                  <li>
                    ระบบสุ่มผลออกเป็นเลขขาว {whitePick} ตัว + เลขแดง 1 ตัว (กติกาแบบ 5+1)
                  </li>
                  <li>
                    จับคู่จากจำนวนเลขขาวที่ตรงกับผล + ว่าเลขแดงตรงหรือไม่ — ไม่สนใจลำดับเลขขาว
                  </li>
                  <li>ระดับรางวัลดูจากตารางด้านล่าง (อาจปรับได้โดยแอดมิน)</li>
                  <li>
                    รางวัลสูงสุด (5 ขาว + แดง) = <strong>แจ็กพอต</strong> จ่ายจากกองแจ็กพอตปัจจุบัน
                    (ไม่เกินเพดานที่ตั้งไว้) แล้วกองจะรีเซ็ตตามค่า seed
                  </li>
                </ul>
                <div className="hj-rules-tiers">
                  {(config.prizeTiers || []).map((tier) => (
                    <div key={`rule-${tier.id}`} className="hj-tier-row">
                      <span>
                        {tier.label || `${tier.whiteMatch} ขาว${tier.redMatch ? ' + แดง' : ''}`}
                      </span>
                      <strong>
                        {describeTierPayout(tier, config.ticketPrice, displayJackpot)}
                      </strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="hj-rules-section">
                <h3>ตัวอย่างการออกรางวัล และแจ็กพอต</h3>
                <div className="hj-rules-examples">
                  <article className="hj-rules-example">
                    <p className="hj-rules-example__title">ตัวอย่างที่ 1 — ไม่ถูกรางวัล</p>
                    <p className="hj-rules-example__line">
                      คุณเลือก <span className="hj-ball hj-ball--white is-selected">1</span>
                      <span className="hj-ball hj-ball--white is-selected">2</span>
                      <span className="hj-ball hj-ball--white is-selected">3</span>
                      <span className="hj-ball hj-ball--white is-selected">4</span>
                      <span className="hj-ball hj-ball--white is-selected">5</span>
                      <span className="hj-ball hj-ball--red is-selected">10</span>
                    </p>
                    <p className="hj-rules-example__line">
                      ผลออก <span className="hj-ball hj-ball--white">11</span>
                      <span className="hj-ball hj-ball--white">22</span>
                      <span className="hj-ball hj-ball--white">33</span>
                      <span className="hj-ball hj-ball--white">44</span>
                      <span className="hj-ball hj-ball--white">55</span>
                      <span className="hj-ball hj-ball--red">20</span>
                    </p>
                    <p className="hj-rules-example__note">ขาวตรง 0 · แดงไม่ตรง → ไม่ได้รางวัล</p>
                  </article>

                  <article className="hj-rules-example">
                    <p className="hj-rules-example__title">ตัวอย่างที่ 2 — ถูกรางวัลระดับกลาง</p>
                    <p className="hj-rules-example__line">
                      คุณเลือก <span className="hj-ball hj-ball--white is-selected">7</span>
                      <span className="hj-ball hj-ball--white is-selected">14</span>
                      <span className="hj-ball hj-ball--white is-selected">21</span>
                      <span className="hj-ball hj-ball--white is-selected">28</span>
                      <span className="hj-ball hj-ball--white is-selected">35</span>
                      <span className="hj-ball hj-ball--red is-selected">8</span>
                    </p>
                    <p className="hj-rules-example__line">
                      ผลออก <span className="hj-ball hj-ball--white">7</span>
                      <span className="hj-ball hj-ball--white">14</span>
                      <span className="hj-ball hj-ball--white">21</span>
                      <span className="hj-ball hj-ball--white">40</span>
                      <span className="hj-ball hj-ball--white">50</span>
                      <span className="hj-ball hj-ball--red">8</span>
                    </p>
                    <p className="hj-rules-example__note">
                      ขาวตรง 3 + แดงตรง → ได้ตามแถว “3 ขาว + แดง” ในตารางรางวัล
                      {(() => {
                        const tier = (config.prizeTiers || []).find(
                          (t) => t.whiteMatch === 3 && t.redMatch,
                        )
                        return tier
                          ? ` (ประมาณ ${describeTierPayout(tier, config.ticketPrice, displayJackpot)})`
                          : ''
                      })()}
                    </p>
                  </article>

                  <article className="hj-rules-example hj-rules-example--jackpot">
                    <p className="hj-rules-example__title">ตัวอย่างที่ 3 — แจ็กพอต</p>
                    <p className="hj-rules-example__line">
                      คุณเลือกเลขขาวครบ 5 ตัวตรงกับผล และเลขแดงตรงด้วย
                    </p>
                    <p className="hj-rules-example__note">
                      ได้แจ็กพอตเต็มกองที่แสดงตอนนี้ ≈ <strong>{formatBaht(displayJackpot)} ฿</strong>
                      (จ่ายตามกองจริงภายใต้เพดาน · หลังแตกกองจะเริ่มใหม่ตาม seed)
                    </p>
                  </article>
                </div>
              </section>
            </div>

            <footer className="hj-rules-modal__footer">
              <button
                type="button"
                className="hj-btn hj-btn--primary"
                onClick={() => setRulesOpen(false)}
              >
                เข้าใจแล้ว ไปเลือกเลข
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default HuayJackpotLobbyPage
